import {
  AlertTitle,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import GenericContainer from '../../../GenericContainer';
import { colors } from '../../../../../shared/constants';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DefaultTooltip from '../../../components/Tooltips/DefaultTooltip';
import React from 'react';
import { ReturnBodyCounts } from './OfferSheetsTable';
import { OfferState } from '../../../../../shared/types/offers';

export const OffersSummaryTable: React.FC<{
  offerCounts: ReturnBodyCounts | null;
  onStateFilterClick: (state: OfferState[]) => void;
  onArchiveFilterClick: () => void;
}> = ({ offerCounts, onStateFilterClick, onArchiveFilterClick }) => {
  const renderCellValues = (offerCount: number) => {
    return `${offerCount}`;
  };

  const rowConfigs = [
    {
      label: 'Keskeneräiset',
      bold: true,
      value: renderCellValues(
        [
          offerCounts?.totalCountDraft,
          offerCounts?.totalCountInternalApproval,
          offerCounts?.totalCountInternalRejection,
          offerCounts?.totalCountReadyForSending,
        ].reduce<number>((sum, count) => sum + (count ?? 0), 0),
      ),
      onClick: () =>
        onStateFilterClick([
          OfferState.Draft,
          OfferState.InternalApproval,
          OfferState.InternalRejection,
          OfferState.ReadyForSending,
        ]),
      children: [
        {
          label: 'Luonnos',
          value: renderCellValues(offerCounts?.totalCountDraft ?? 0),
          onClick: () => onStateFilterClick([OfferState.Draft]),
        },
        {
          label: 'Sisäinen hyväksyntä',
          value: renderCellValues(
            offerCounts?.totalCountInternalApproval ?? 0,
          ),
          onClick: () =>
            onStateFilterClick([OfferState.InternalApproval]),
        },
        {
          label: 'Palautettu',
          value: renderCellValues(
            offerCounts?.totalCountInternalRejection ?? 0,
          ),
          onClick: () =>
            onStateFilterClick([OfferState.InternalRejection]),
        },
        {
          label: 'Valmis lähtettäväksi',
          value: renderCellValues(
            offerCounts?.totalCountReadyForSending ?? 0,
          ),
          onClick: () =>
            onStateFilterClick([OfferState.ReadyForSending]),
        },
      ],
    },
    {
      label: 'Lähetetty asiakkaalle',
      bold: true,
      value: renderCellValues(
        [
          offerCounts?.totalCountSent,
          offerCounts?.totalCountExpired,
          offerCounts?.totalCountApproved,
          offerCounts?.totalCountRejected,
        ].reduce<number>((sum, count) => sum + (count ?? 0), 0),
      ),
      onClick: () =>
        onStateFilterClick([
          OfferState.Sent,
          OfferState.Expired,
          OfferState.Approved,
          OfferState.Rejected,
        ]),
      children: [
        {
          label: 'Lähetetty',
          value: (
            <>{renderCellValues(offerCounts?.totalCountSent ?? 0)}</>
          ),
          onClick: () => onStateFilterClick([OfferState.Sent]),
        },
        {
          label: 'Vanhentunut',
          value: (
            <>
              {renderCellValues(offerCounts?.totalCountExpired ?? 0)}
            </>
          ),
          onClick: () => onStateFilterClick([OfferState.Expired]),
        },
        {
          label: 'Hyväksytty',
          value: (
            <>
              {renderCellValues(offerCounts?.totalCountApproved ?? 0)}
            </>
          ),
          onClick: () => onStateFilterClick([OfferState.Approved]),
        },
        {
          label: 'Hylätty',
          value: (
            <>
              {renderCellValues(offerCounts?.totalCountRejected ?? 0)}
            </>
          ),
          onClick: () => onStateFilterClick([OfferState.Rejected]),
        },
      ],
    },
    {
      label: 'Arkistoidut',
      bold: true,
      value: renderCellValues(offerCounts?.totalArchived ?? 0),
      onClick: onArchiveFilterClick,
    },
    {
      label: 'Yhteensä',
      bold: true,
      value: [
        offerCounts?.totalCountDraft,
        offerCounts?.totalCountInternalApproval,
        offerCounts?.totalCountInternalRejection,
        offerCounts?.totalCountReadyForSending,
        offerCounts?.totalCountSent,
        offerCounts?.totalCountExpired,
        offerCounts?.totalCountApproved,
        offerCounts?.totalCountRejected,
        offerCounts?.totalArchived,
      ].reduce<number>((sum, count) => sum + (count ?? 0), 0),
    },
  ];

  const renderRows = (configs: typeof rowConfigs, indent = 0) =>
    configs.map(
      ({ label, value, onClick, bold, children }: any, idx) => (
        <React.Fragment key={`${label}-${idx}`}>
          <TableRow sx={{ borderBottom: 'none' }}>
            <TableCell
              sx={{
                borderBottom: 'none',
                fontWeight: bold ? 'bold' : undefined,
                paddingLeft: indent ? 4 : undefined,
              }}
            >
              {label}
            </TableCell>
            <TableCell
              sx={{
                borderBottom: 'none',
                fontWeight: bold ? 'bold' : undefined,
                cursor: onClick ? 'pointer' : 'default',
                color: onClick ? 'blue' : 'inherit',
              }}
              onClick={onClick}
            >
              {value}
            </TableCell>
          </TableRow>
          {children && renderRows(children, indent + 1)}
        </React.Fragment>
      ),
    );

  return (
    <Box sx={{ mt: -8 }}>
      <GenericContainer
        borderColor={colors.ramiOrange}
        border
        legendTitle="Yhteenveto"
        legendIcon={
          <ArticleOutlinedIcon
            sx={{ fontSize: 20, color: 'text.secondary' }}
          />
        }
      >
        <AlertTitle sx={{ fontSize: 20 }}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Typography>Omat tarjoukset</Typography>
            <DefaultTooltip title="Taulukosta näet yhteenvedon tarjouksista, joissa olet tekijä">
              <HelpOutlineIcon
                sx={{
                  fontSize: 20,
                  color: 'text.secondary',
                  cursor: 'help',
                }}
              />
            </DefaultTooltip>
          </Box>
        </AlertTitle>
        <Box sx={{ width: 300 }}>
          <Table size="small">
            <TableBody>{renderRows(rowConfigs)}</TableBody>
          </Table>
        </Box>
      </GenericContainer>
    </Box>
  );
};
